import styled from "styled-components";
import Card from "common/Boxes/Card";
import Tabs from "common/Tabs/Tabs";
import {
  BotGraphType,
  BotType,
  LanguageNameSpaces,
  TimeRange,
} from "@shared/types";
import ChartTimeRangeButtons from "Global/ChartTimeRangeButtons";
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { BotGraphTypeToDisplay } from "@shared/features/bots/constants";
import LineChart from "common/Charts/LineChart/LineChart";
import BarChart from "common/Charts/BarChart";
import { useGetBotsGraphsData } from "@shared/features/bots/hooks";
import { useIncludeArchived } from "features/filters/hooks";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { TIME_RANGE_1_MONTHS } from "@shared/common/constants";
import ExpandingChart from "features/dashboard/MobileDashboard/ExpandingChart.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_LAYOUT } from "features/bots/common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface BotsGraphTabsProps {
  botType: BotType;
  expanded?: boolean;
}

const BotsGraphTabs = forwardRef(
  ({ botType, expanded }: BotsGraphTabsProps, ref) => {
    const { getDisplayCurrencyAmountPrecision } = usePrecisions();
    const profitPrecision = getDisplayCurrencyAmountPrecision();
    const displayCurrency = useSelector(selectDisplayCurrency);
    const isMobile = useMedia(
      [`(max-width: ${MOBILE_LAYOUT}px)`],
      [true],
      false
    );
    const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

    const [tab, setTab] = useState<BotGraphType>(
      BotGraphType.ACCUMULATING_PROFIT
    );
    const [timeRange, setTimeRange] = useState<TimeRange>(TIME_RANGE_1_MONTHS);
    const includeArchived = useIncludeArchived(botType);

    const getBotsGraphsArgs = useMemo(() => {
      return {
        bot_type: botType,
        time_range: timeRange,
        include_archived: includeArchived,
      };
    }, [botType, timeRange, includeArchived]);

    const graphData = useGetBotsGraphsData(getBotsGraphsArgs);
    const loading = graphData.status.isFetching;
    const isSuccess = graphData.status.isSuccess;

    useImperativeHandle(ref, () => ({
      refetch: () => {
        try {
          graphData.refetch();
        } catch {}
      },
    }));

    const accumulatedProfitChart = (
      <ChartWrapper>
        <LineChart
          data={graphData.data.accumulatedProfit}
          loading={loading}
          displayCurrency={displayCurrency}
          precision={profitPrecision}
          isSuccess={isSuccess}
        />
      </ChartWrapper>
    );

    const profitByDayChart = (
      <ChartWrapper>
        <BarChart
          data={graphData.data.profitByDay}
          loading={loading}
          displayCurrency={displayCurrency}
          usePositiveNegativeColorScheme={true}
          precision={profitPrecision}
          isSuccess={isSuccess}
        />
      </ChartWrapper>
    );

    const tabsItems = [
      {
        key: BotGraphType.ACCUMULATING_PROFIT.toString(),
        label: t(BotGraphTypeToDisplay[BotGraphType.ACCUMULATING_PROFIT]),
        children: accumulatedProfitChart,
        data: graphData.data.accumulatedProfit,
      },
      {
        key: BotGraphType.PROFIT_BY_DAY.toString(),
        label: t(BotGraphTypeToDisplay[BotGraphType.PROFIT_BY_DAY]),
        children: profitByDayChart,
        data: graphData.data.profitByDay,
        isBarChart: true,
      },
    ];

    if (isMobile) {
      return (
        <ExpandingChart
          tabsItems={tabsItems}
          selectedTimeRange={timeRange}
          setSelectedTimeRange={setTimeRange}
          precision={profitPrecision}
          loading={loading}
          isSuccess={isSuccess}
          expanded={expanded}
          usePositiveNegativeColorScheme={true}
        />
      );
    }

    return (
      <ChartCard style={{ padding: "8px 16px 16px 16px" }}>
        <Tabs
          activeKey={tab.toString()}
          onTabClick={(tabKey) => setTab(parseInt(tabKey))}
          size="small"
          tabBarStyle={{
            margin: "0 0 8px 0",
          }}
          $hideBorder={true}
          items={tabsItems}
        />
        <ChartTimeRangeButtons
          selectedTimeRange={timeRange}
          handleSelectTimeRange={(timeRange: TimeRange) => {
            setTimeRange(timeRange);
          }}
          buttonSize={"medium"}
        />
      </ChartCard>
    );
  }
);

export default BotsGraphTabs;

const ChartCard = styled(Card)`
  overflow: hidden;
  padding: 8px 16px 16px 16px;
`;
const ChartWrapper = styled.div`
  height: 200px;
  margin-top: 8px;
`;
